import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Building2, ArrowLeft, ArrowRight,
  Loader2, Download, CheckCircle, AlertCircle, ChevronDown, Receipt, Shield,
  Sparkles, Table, Calendar, Hash, User, Phone, MapPin, Landmark, CreditCard,
} from 'lucide-react';
import { ContractTemplate, CompanyInfo, ContractTerms, TEMPLATE_CATEGORIES } from '@/types';
import { loadFromLocalStorage, generateContractNumber, formatDate, getAllTemplates, saveContractRecord, getEmployeeInfo } from '@/utils/storage';

interface ClausePreset {
  id: string;
  name: string;
  terms: ContractTerms;
  createdAt: string;
}
import { downloadBlob } from '@/utils/contractGenerator';
import { scanContractBlob, RiskScanResult } from '@/utils/riskScanner';
import { extractTextFromDocxBlob, performLegalReview, AILegalReviewResult, generateContractWithAI, convertMarkdownToDocx } from '@/utils/aiService';

// 生成步骤（合并后只需 4 步）
type Step = 1 | 2 | 3 | 4;

export default function ContractGenerator() {
  const navigate = useNavigate();

  // 状态
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const allTemplates = useMemo(() => getAllTemplates(), []);
  const [companies] = useState<CompanyInfo[]>(() => {
    return loadFromLocalStorage<CompanyInfo[]>('contract_companies') || [];
  });

  const ourCompanies = useMemo(() =>
    companies.filter(c => c.role === 'partyA' || c.role === 'both'),
    [companies]
  );
  const partyBCompanies = useMemo(() =>
    companies.filter(c => c.role === 'partyB' || c.role === 'both'),
    [companies]
  );

  // 表单
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedPartyBId, setSelectedPartyBId] = useState('');
  const [contractNumber, setContractNumber] = useState(generateContractNumber());
  const [signingDate, setSigningDate] = useState(formatDate(new Date()));
  const [employeeName, setEmployeeName] = useState('');

  // 分类筛选
  const [selectedCategory, setSelectedCategory] = useState<string>('全部');

  // 生成结果
  const [generatedBlob, setGeneratedBlob] = useState<Blob | null>(null);
  const [generatedText, setGeneratedText] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [progressMsg, setProgressMsg] = useState('');

  // 法审结果
  const [riskResult, setRiskResult] = useState<RiskScanResult | null>(null);
  const [aiResult, setAiResult] = useState<AILegalReviewResult | null>(null);
  const [isReviewing, setIsReviewing] = useState(false);

  // 读取员工信息
  useEffect(() => {
    const emp = getEmployeeInfo();
    if (emp) {
      setEmployeeName(emp.name || '');
    }
  }, []);

  // 核心条款（优先从草稿加载，也可通过预设选择）
  const [clauseDraft, setClauseDraft] = useState<Partial<ContractTerms>>({});
  useEffect(() => {
    const draft = loadFromLocalStorage<ContractTerms>('contract_clause_draft');
    if (draft) setClauseDraft(draft);
  }, []);

  // 读取核心条款预设列表
  const [clausePresets, setClausePresets] = useState<ClausePreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  useEffect(() => {
    const presets = loadFromLocalStorage<ClausePreset[]>('contract_clause_presets');
    if (presets) setClausePresets(presets);
  }, []);

  // 加载选中的预设
  const handleLoadPreset = (presetId: string) => {
    setSelectedPresetId(presetId);
    if (!presetId) return;
    const preset = clausePresets.find(p => p.id === presetId);
    if (preset) {
      setClauseDraft({ ...preset.terms });
    }
  };

  // 选中的模板和公司
  const selectedTemplate = useMemo(() =>
    allTemplates.find(t => t.id === selectedTemplateId),
    [allTemplates, selectedTemplateId]
  );
  const selectedCompany = useMemo(() =>
    companies.find(c => c.id === selectedCompanyId),
    [companies, selectedCompanyId]
  );
  const selectedPartyB = useMemo(() =>
    companies.find(c => c.id === selectedPartyBId),
    [companies, selectedPartyBId]
  );

  // ========== 分类筛选 ==========
  const categoriesWithTemplates = useMemo(() => {
    const result: { category: string; templates: ContractTemplate[] }[] = [];
    for (const t of allTemplates) {
      let cat = t.category || '其他';
      const group = result.find(r => r.category === cat);
      if (group) {
        group.templates.push(t);
      } else {
        result.push({ category: cat, templates: [t] });
      }
    }
    return result;
  }, [allTemplates]);

  const filteredTemplates = useMemo(() => {
    if (selectedCategory === '全部') return allTemplates;
    return allTemplates.filter(t => (t.category || '其他') === selectedCategory);
  }, [allTemplates, selectedCategory]);

  // 分期付款验证
  const validateInstallments = useCallback((): string => {
    if (clauseDraft?.paymentMethod === '分期付款' && clauseDraft?.installments && clauseDraft.installments.length > 0) {
      const totalAmount = parseFloat(clauseDraft.totalAmount || '0') || 0;
      const installmentTotal = clauseDraft.installments.reduce((sum, inst) => sum + (parseFloat(inst.amount || '0') || 0), 0);
      if (installmentTotal > totalAmount) {
        return '分期付款总金额（¥' + installmentTotal.toFixed(2) + '）超过采购总金额（¥' + totalAmount.toFixed(2) + '），请调整分期比例';
      }
      const percentageTotal = clauseDraft.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage?.replace('%', '') || '0') || 0), 0);
      if (percentageTotal > 100) {
        return '分期付款比例之和（' + percentageTotal + '%）超过100%，请调整';
      }
    }
    return '';
  }, [clauseDraft]);

  // ========== 生成合同（AI） ==========
  const handleGenerate = useCallback(async () => {
    const err = validateInstallments();
    if (err) {
      setGenerateError(err);
      return;
    }
    setIsGenerating(true);
    setGenerateError('');
    setProgressMsg('正在准备生成合同...');

    try {
      const template = selectedTemplate;
      const ourCompany = selectedCompany;
      const partyB = selectedPartyB;

      if (!template) throw new Error('请选择合同模板');
      if (!ourCompany) throw new Error('请选择我方公司');
      if (!partyB) throw new Error('请选择乙方公司');

      // 读取模板内容作为参考
      let templateText = '';
      try {
        const resp = await fetch(template.systemPath || '');
        if (resp.ok) {
          const buf = await resp.arrayBuffer();
          const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          templateText = await extractTextFromDocxBlob(blob);
        }
      } catch (e) {
        console.warn('读取模板参考文本失败', e);
      }

      setProgressMsg('AI 正在生成合同全文（第1步/2步）...');

      // 构造 AI 生成参数
      const params: any = {
        templateName: template.name,
        templateCategory: template.category,
        templateText: templateText,
        partyA: {
          全称: ourCompany.fullName,
          统一社会信用代码: ourCompany.creditCode,
          法定代表人: ourCompany.legalRepresentative,
          注册地址: ourCompany.registeredAddress,
          办公地址: ourCompany.officeAddress,
          联系电话: ourCompany.phone,
          开户银行: ourCompany.bankName,
          银行账号: ourCompany.bankAccount,
        },
        partyB: {
          全称: partyB.fullName,
          统一社会信用代码: partyB.creditCode,
          法定代表人: partyB.legalRepresentative,
          注册地址: partyB.registeredAddress,
          办公地址: partyB.officeAddress,
          联系电话: partyB.phone,
          开户银行: partyB.bankName,
          银行账号: partyB.bankAccount,
        },
        terms: {
          paymentMethod: clauseDraft?.paymentMethod || '一次性付款',
          paymentDetails: clauseDraft?.paymentDetails || '',
          validityPeriod: clauseDraft?.validityPeriod || '',
          specialClauses: clauseDraft?.specialClauses || '',
          totalAmount: clauseDraft?.totalAmount || '',
          totalAmountUppercase: clauseDraft?.totalAmountUppercase || '',
          purchaseItems: clauseDraft?.purchaseItems || [],
          installments: clauseDraft?.installments || [],
        },
        employeeName,
        contractNumber,
        signingDate,
      };

      const aiResult = await generateContractWithAI(params, (_stage, msg) => {
        setProgressMsg(msg);
      });

      if (aiResult.error) throw new Error(aiResult.error);

      setGeneratedText(aiResult.contractMarkdown || '');

      // 将 AI 生成的 Markdown 转为 Word Blob
      setProgressMsg('正在生成 Word 文档...');
      const docxBlob = await convertMarkdownToDocx(
        aiResult.contractMarkdown || '',
        template.name
      );
      setGeneratedBlob(docxBlob);

      setProgressMsg('合同生成完成！正在进行法审...');

      // 自动触发法审
      await handleReview(aiResult.contractMarkdown || '');

      setCurrentStep(4);
    } catch (err: any) {
      setGenerateError(err.message || '生成失败');
    } finally {
      setIsGenerating(false);
      setProgressMsg('');
    }
  }, [selectedTemplate, selectedCompany, selectedPartyB, clauseDraft, employeeName, contractNumber, signingDate]);

  // ========== 法审 ==========
  const handleReview = useCallback(async (contractText: string) => {
    setIsReviewing(true);
    try {
      if (generatedBlob) {
        const risk = await scanContractBlob(generatedBlob);
        setRiskResult(risk);
      }
      const ai = await performLegalReview(contractText);
      setAiResult(ai);
    } catch (err: any) {
      console.error('法审失败', err);
    } finally {
      setIsReviewing(false);
    }
  }, [generatedBlob]);

  // ========== 下载合同 ==========
  const handleDownload = useCallback(() => {
    if (!generatedBlob) return;
    downloadBlob(generatedBlob, `合同-${contractNumber}.docx`);
  }, [generatedBlob, contractNumber]);

  // ========== 渲染 ==========

  if (isGenerating) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center space-y-6">
        <div className="relative">
          <Loader2 className="w-16 h-16 text-primary animate-spin" />
          <Sparkles className="w-6 h-6 text-yellow-400 absolute -top-1 -right-1 animate-pulse" />
        </div>
        <h2 className="text-xl font-bold text-gray-800">AI 正在生成合同</h2>
        <div className="w-64 bg-gray-100 rounded-full h-2.5 overflow-hidden">
          <div className="bg-primary h-2.5 rounded-full animate-pulse" style={{ width: '60%' }}></div>
        </div>
        <p className="text-sm text-gray-500 animate-pulse">{progressMsg || '预计需要 20-40 秒...'}</p>
        <div className="text-xs text-gray-400 space-y-1 text-center">
          <p>✓ 参考模板结构</p>
          <p>⏳ 生成合同条款中...</p>
          <p>⏳ 进行法审检查中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 顶部 */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/')} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          生成合同（AI 智能生成）
        </h1>
      </div>

      {/* 步骤条 */}
      <div className="flex items-center gap-2">
        {[1, 2, 3, 4].map((s) => (
          <div key={s} className={`flex-1 h-1.5 rounded-full ${
            s < currentStep ? 'bg-primary' : s === currentStep ? 'bg-primary/50 animate-pulse' : 'bg-gray-200'
          }`} />
        ))}
      </div>

      {/* 步骤1：选模板 —— 分类筛选 + 卡片 */}
      {currentStep === 1 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold">① 选择合同模板</h2>

          {/* 分类筛选按钮 */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory('全部')}
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === '全部'
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              全部
            </button>
            {categoriesWithTemplates.map(cat => (
              <button
                key={cat.category}
                onClick={() => setSelectedCategory(cat.category)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.category
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.category} ({cat.templates.length})
              </button>
            ))}
          </div>

          {/* 模板卡片 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredTemplates.map(t => (
              <div
                key={t.id}
                onClick={() => setSelectedTemplateId(t.id)}
                className={`p-4 border rounded-xl cursor-pointer transition ${
                  selectedTemplateId === t.id
                    ? 'border-primary bg-primary/5 shadow-sm ring-2 ring-primary/20'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className={`w-5 h-5 ${selectedTemplateId === t.id ? 'text-primary' : 'text-gray-400'}`} />
                  <div className="font-medium text-gray-800">{t.name}</div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                    {t.category}
                  </span>
                  {t.isSystem && (
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                      系统模板
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-8 text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>该分类下暂无模板</p>
            </div>
          )}

          <button
            onClick={() => setCurrentStep(2)}
            disabled={!selectedTemplateId}
            className="mt-4 px-5 py-2.5 bg-primary text-white rounded-lg disabled:bg-gray-300"
          >
            下一步 <ArrowRight className="w-4 h-4 inline ml-1" />
          </button>
        </div>
      )}

      {/* 步骤2：填公司信息 */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold">② 填写合同信息</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 我方公司 */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <h3 className="font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                我方公司（甲方）
              </h3>
              <select
                value={selectedCompanyId}
                onChange={e => setSelectedCompanyId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">-- 选择我方公司 --</option>
                {ourCompanies.map(c => (
                  <option key={c.id} value={c.id}>{c.fullName}</option>
                ))}
              </select>
              {selectedCompany && (
                <div className="text-sm text-gray-500 space-y-1 bg-gray-50 rounded-lg p-3">
                  <p><span className="text-gray-400">法人：</span>{selectedCompany.legalRepresentative || '--'}</p>
                  <p><span className="text-gray-400">地址：</span>{selectedCompany.registeredAddress || '--'}</p>
                  <p><span className="text-gray-400">电话：</span>{selectedCompany.phone || '--'}</p>
                </div>
              )}
            </div>
            {/* 乙方公司 */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <h3 className="font-medium text-gray-700 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                对方公司（乙方）
              </h3>
              <select
                value={selectedPartyBId}
                onChange={e => setSelectedPartyBId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">-- 选择对方公司 --</option>
                {partyBCompanies.map(c => (
                  <option key={c.id} value={c.id}>{c.fullName}</option>
                ))}
              </select>
              {selectedPartyB && (
                <div className="text-sm text-gray-500 space-y-1 bg-gray-50 rounded-lg p-3">
                  <p><span className="text-gray-400">法人：</span>{selectedPartyB.legalRepresentative || '--'}</p>
                  <p><span className="text-gray-400">地址：</span>{selectedPartyB.registeredAddress || '--'}</p>
                  <p><span className="text-gray-400">电话：</span>{selectedPartyB.phone || '--'}</p>
                </div>
              )}
            </div>
          </div>

          {/* 合同编号 + 日期 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">合同编号</label>
              <input
                type="text"
                value={contractNumber}
                onChange={e => setContractNumber(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">签订日期</label>
              <input
                type="date"
                value={signingDate}
                onChange={e => setSigningDate(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          {/* 核心条款预设 */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <label className="block text-sm font-medium text-amber-800 mb-2">
              <Receipt className="w-4 h-4 inline mr-1" />
              加载核心条款预设（可选，不选则生成基础合同）
            </label>
            <div className="flex gap-3 items-center">
              <select
                value={selectedPresetId}
                onChange={e => handleLoadPreset(e.target.value)}
                className="flex-1 px-3 py-2 border border-amber-200 rounded-lg bg-white"
              >
                <option value="">-- 选择已保存的核心条款预设 --</option>
                {clausePresets.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {selectedPresetId && (
                <button
                  onClick={() => { setSelectedPresetId(''); setClauseDraft({}); }}
                  className="text-sm text-amber-600 hover:text-amber-800"
                >
                  清除
                </button>
              )}
            </div>
            {clausePresets.length === 0 && (
              <p className="text-xs text-amber-600 mt-2">
                暂无预设，请先在「核心条款」页面创建并保存预设。
              </p>
            )}
            {selectedPresetId && clauseDraft.paymentMethod && (
              <div className="mt-2 text-xs text-amber-700">
                已加载：付款方式「{clauseDraft.paymentMethod}」· 有效期「{clauseDraft.validityPeriod || '未填'}」· 总金额¥{clauseDraft.totalAmount || '—'}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <button onClick={() => setCurrentStep(1)} className="px-5 py-2.5 border border-gray-300 rounded-lg">上一步</button>
            <button
              onClick={() => setCurrentStep(3)}
              disabled={!selectedCompanyId || !selectedPartyBId}
              className="px-5 py-2.5 bg-primary text-white rounded-lg disabled:bg-gray-300"
            >
              下一步 <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* 步骤3：确认核心条款 —— 显示完整信息 */}
      {currentStep === 3 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold">③ 确认合同信息</h2>

          {/* 模板 + 双方信息概览 */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                基本信息
              </h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <p><span className="text-gray-400">合同模板：</span><span className="font-medium">{selectedTemplate?.name || '--'}</span></p>
                <p><span className="text-gray-400">合同编号：</span>{contractNumber}</p>
                <p><span className="text-gray-400">签订日期：</span>{signingDate}</p>
              </div>
              <div className="space-y-2">
                <p><span className="text-gray-400">合同类型：</span>{selectedTemplate?.category || '--'}</p>
                <p><span className="text-gray-400">经办人：</span>{employeeName || '--'}</p>
              </div>
            </div>
          </div>

          {/* 甲方信息 */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-red-50 to-orange-50 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-red-500" />
                甲方（我方）— {selectedCompany?.fullName || '--'}
              </h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p><span className="text-gray-400">统一社会信用代码：</span>{selectedCompany?.creditCode || '--'}</p>
              <p><span className="text-gray-400">法定代表人：</span>{selectedCompany?.legalRepresentative || '--'}</p>
              <p><span className="text-gray-400">注册地址：</span>{selectedCompany?.registeredAddress || '--'}</p>
              <p><span className="text-gray-400">办公地址：</span>{selectedCompany?.officeAddress || '--'}</p>
              <p><span className="text-gray-400">联系电话：</span>{selectedCompany?.phone || '--'}</p>
              <p><span className="text-gray-400">开户银行：</span>{selectedCompany?.bankName || '--'}</p>
              <p><span className="text-gray-400">银行账号：</span>{selectedCompany?.bankAccount || '--'}</p>
            </div>
          </div>

          {/* 乙方信息 */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-500" />
                乙方 — {selectedPartyB?.fullName || '--'}
              </h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <p><span className="text-gray-400">统一社会信用代码：</span>{selectedPartyB?.creditCode || '--'}</p>
              <p><span className="text-gray-400">法定代表人：</span>{selectedPartyB?.legalRepresentative || '--'}</p>
              <p><span className="text-gray-400">注册地址：</span>{selectedPartyB?.registeredAddress || '--'}</p>
              <p><span className="text-gray-400">办公地址：</span>{selectedPartyB?.officeAddress || '--'}</p>
              <p><span className="text-gray-400">联系电话：</span>{selectedPartyB?.phone || '--'}</p>
              <p><span className="text-gray-400">开户银行：</span>{selectedPartyB?.bankName || '--'}</p>
              <p><span className="text-gray-400">银行账号：</span>{selectedPartyB?.bankAccount || '--'}</p>
            </div>
          </div>

          {/* 核心条款 */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                <Receipt className="w-4 h-4 text-green-600" />
                核心条款
              </h3>
            </div>
            <div className="p-4 space-y-4 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-400">付款方式：</span>
                  <span className="font-medium">{clauseDraft?.paymentMethod || '未设置'}</span>
                </div>
                <div>
                  <span className="text-gray-400">合同有效期：</span>
                  <span className="font-medium">{clauseDraft?.validityPeriod || '未设置'}</span>
                </div>
                <div>
                  <span className="text-gray-400">总金额：</span>
                  <span className="font-medium text-red-600">¥{clauseDraft?.totalAmount || '--'}</span>
                  {clauseDraft?.totalAmountUppercase && (
                    <span className="text-xs text-gray-400 ml-2">（{clauseDraft.totalAmountUppercase}）</span>
                  )}
                </div>
                <div>
                  <span className="text-gray-400">付款详情：</span>
                  <span className="font-medium">{clauseDraft?.paymentDetails || '--'}</span>
                </div>
              </div>

              {/* 分期付款明细 */}
              {clauseDraft?.paymentMethod === '分期付款' && clauseDraft?.installments && clauseDraft.installments.length > 0 && (
                <div className="border-t border-gray-100 pt-3">
                  <h4 className="font-medium text-gray-700 mb-2">分期付款明细</h4>
                  {(() => {
                    const totalAmount = parseFloat(clauseDraft.totalAmount || '0') || 0;
                    const installmentTotal = clauseDraft.installments.reduce((sum, inst) => sum + (parseFloat(inst.amount || '0') || 0), 0);
                    const percentageTotal = clauseDraft.installments.reduce((sum, inst) => sum + (parseFloat(inst.percentage?.replace('%', '') || '0') || 0), 0);
                    const isOverAmount = installmentTotal > totalAmount;
                    const isOverPercent = percentageTotal > 100;
                    return (
                      <>
                        {(isOverAmount || isOverPercent) && (
                          <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                            {isOverAmount && `⚠️ 分期总金额 ¥${installmentTotal.toFixed(2)} 超过采购总金额 ¥${totalAmount.toFixed(2)}`}
                            {isOverAmount && isOverPercent && '；'}
                            {isOverPercent && `⚠️ 分期比例之和 ${percentageTotal}% 超过100%`}
                          </div>
                        )}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-gray-500">期数</th>
                                <th className="px-3 py-2 text-left text-gray-500">付款比例</th>
                                <th className="px-3 py-2 text-left text-gray-500">付款金额</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {clauseDraft.installments.map((inst, i) => (
                                <tr key={i}>
                                  <td className="px-3 py-2">第{inst.period}期</td>
                                  <td className="px-3 py-2">{inst.percentage}</td>
                                  <td className="px-3 py-2">¥{inst.amount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        <div className="mt-1 text-xs text-gray-400">
                          分期总额：¥{installmentTotal.toFixed(2)} / 采购总额：¥{totalAmount.toFixed(2)}（{percentageTotal}%）
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}

              {/* 特殊条款 */}
              {clauseDraft?.specialClauses && (
                <div className="border-t border-gray-100 pt-3">
                  <h4 className="font-medium text-gray-700 mb-1">特殊条款</h4>
                  <p className="text-gray-600 whitespace-pre-wrap">{clauseDraft.specialClauses}</p>
                </div>
              )}
            </div>
          </div>

          {/* 采购明细 */}
          {clauseDraft?.purchaseItems && clauseDraft.purchaseItems.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <Table className="w-4 h-4 text-purple-600" />
                  采购明细（共 {clauseDraft.purchaseItems.length} 条）
                </h3>
              </div>
              <div className="overflow-x-auto p-4">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-500">产品名称</th>
                      <th className="px-3 py-2 text-left text-gray-500">规格型号</th>
                      <th className="px-3 py-2 text-left text-gray-500">数量</th>
                      <th className="px-3 py-2 text-left text-gray-500">单价</th>
                      <th className="px-3 py-2 text-left text-gray-500">金额</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {clauseDraft.purchaseItems.map((item, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2">{item.产品名称 || '--'}</td>
                        <td className="px-3 py-2">{item.规格型号 || '--'}</td>
                        <td className="px-3 py-2">{item.数量 || '--'}</td>
                        <td className="px-3 py-2">¥{item.单价 || '--'}</td>
                        <td className="px-3 py-2 font-medium">¥{item.金额 || '--'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button onClick={() => setCurrentStep(2)} className="px-5 py-2.5 border border-gray-300 rounded-lg">上一步</button>
            <button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-gray-300"
            >
              <Sparkles className="w-4 h-4" />
              生成合同
            </button>
          </div>
        </div>
      )}

      {/* 步骤4：结果 + 法审 */}
      {currentStep === 4 && generatedBlob && (
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 flex items-center gap-4">
            <CheckCircle className="w-10 h-10 text-green-500" />
            <div>
              <h2 className="text-lg font-bold text-green-800">合同生成成功！</h2>
              <p className="text-sm text-green-600">已通过 AI 法审，可直接下载使用。</p>
            </div>
            <button
              onClick={handleDownload}
              className="ml-auto flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-lg"
            >
              <Download className="w-4 h-4" />
              下载合同
            </button>
          </div>

          {/* 法审结果 */}
          {(riskResult || aiResult) && (
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-4 py-3 bg-gradient-to-r from-primary/5 to-primary/10 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-primary" />
                  AI 法务审查报告
                </h3>
              </div>
              <div className="p-4 space-y-4">
                {aiResult && (
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className={`text-4xl font-bold ${
                      aiResult.overallScore >= 80 ? 'text-green-600' :
                      aiResult.overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {aiResult.overallScore}
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-700">合规评分</div>
                      <div className="text-xs text-gray-500">{aiResult.summary}</div>
                    </div>
                  </div>
                )}
                {riskResult && (
                  <div className="text-sm text-gray-600">
                    规则引擎扫描：共 {riskResult.total} 项风险（高 {riskResult.highCount} / 中 {riskResult.mediumCount} / 低 {riskResult.lowCount}）
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={() => { setCurrentStep(1); setGeneratedBlob(null); setAiResult(null); }}
              className="px-5 py-2.5 border border-gray-300 rounded-lg"
            >
              重新生成
            </button>
            <button
              onClick={() => navigate('/records')}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              查看合同报表
            </button>
          </div>
        </div>
      )}

      {/* 错误提示 */}
      {generateError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{generateError}</p>
        </div>
      )}
    </div>
  );
}
